import { useMemo } from "react";
import { FrameManager } from "../../Components/FramePlayer.js";
import { useQueue } from "../../Context/QueueProvider.tsx";
import { borderRadius, colors } from "../../styles/theme.js";
import PlaylistItem from "../PlaylistBuilder/Components/PlaylistItem.js";
import LoadingSpinner from "../../Components/LoadingSpinner.js";

const styles = {
  emptyContainer: {
    padding: "20px",
    maxWidth: "00px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
  },
  emptyHeader: {
    marginBottom: "10px",
  },
  emptyText: {
    color: colors.textSecondary,
    fontStyle: "italic",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    marginTop: "80px",
    flexGrow: 1,
    flexShrink: 1,
    height: "100%",
  },
  playlistWrapper: {
    width: "1100px",
    gap: "42px",
    paddingLeft: "40px",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "row",
    borderRadius: borderRadius.lg,
    backgroundColor: colors.secondaryBackground,
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
  },
  videoSection: {
    display: "flex",
    flexDirection: "column",
    minWidth: 600,
    overflow: "hidden",
  },
  videoHeader: {
    marginBottom: "10px",
    color: colors.text,
  },
  videoCount: {
    color: colors.textSecondary,
  },
  videoFrameWrapper: {
    width: "100%",
    minWidth: "640px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "center",
  },
  frameManager: {
    width: "100%",
    height: "360px",
    position: "relative",
  },
  playlistColumn: {
    borderLeft: `1px solid ${colors.secondary}`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "center",
  },
  previousSongsWrapper: {
    borderRadius: `0  ${borderRadius.lg}px 0 0`,
    overflow: "hidden",
  },
  nextSongsWrapper: {
    borderRadius: `0 0 ${borderRadius.lg}px 0`,
    overflow: "hidden",
  },
};

export const PlaylistScreen = () => {
  const {
    playlist,
    playingIndex,
    queue,
    videoData,
    stationName: playlistName,
    isLoading,
  } = useQueue();

  const isUserPlaylist = useMemo(() => {
    return (
      new URLSearchParams(window.location.search).get("userPlaylist") === "true"
    );
  }, []);

  const titleText = useMemo(() => {
    return isUserPlaylist ? playlistName : playlistName + " Radio";
  }, [isUserPlaylist, playlistName]);

  const songsArray = useMemo(() => {
    return Array.isArray(playlist) ? playlist : [];
  }, [playlist]);

  const previousSongs = useMemo(() => {
    if (playingIndex - 3 <= 0) {
      return [
        ...queue
          .slice(queue.length - (3 - playingIndex), queue.length)
          .map((videoId) => videoData[videoId]),
        ...queue.slice(0, playingIndex).map((videoId) => videoData[videoId]),
      ];
    }

    return queue
      .slice(playingIndex - 3, playingIndex)
      .map((videoId) => videoData[videoId]);
  }, [queue, playingIndex, videoData]);

  const nextSongs = useMemo(() => {
    if (playingIndex + 3 >= queue.length) {
      console.log({
        first: queue
          .slice(playingIndex + 1, queue.length)
          .map((videoId) => videoData[videoId]),
        last: queue
          .slice(0, 3 - (queue.length - playingIndex) + 1)
          .map((videoId) => videoData[videoId]),
        index: 3 - (queue.length - playingIndex) + 1,
      });
      return [
        ...queue
          .slice(playingIndex + 1, queue.length)
          .map((videoId) => videoData[videoId]),
        ...queue
          .slice(0, 3 - (queue.length - playingIndex) + 1)
          .map((videoId) => videoData[videoId]),
      ];
    }

    return queue
      .slice(playingIndex + 1, playingIndex + 4)
      .map((videoId) => videoData[videoId]);
  }, [queue, playingIndex, videoData]);

  const currentSong = useMemo(() => {
    return videoData[queue[playingIndex]];
  }, [playingIndex, queue, videoData]);

  // Handle clicking on a song to open YouTube video
  const handleSongClick = (videoId) => {
    if (videoId) {
      const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
      window.open(youtubeUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingSpinner message="Loading playlist..." />
      </div>
    );
  }

  if (songsArray.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <h1 style={styles.emptyHeader}>{playlistName || "Playlist"}</h1>
        <p style={styles.emptyText}>No songs in this playlist</p>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.playlistWrapper}>
        <div style={styles.videoSection}>
          <h1 style={styles.videoHeader}>{titleText}</h1>
          {isUserPlaylist && (
            <p style={styles.videoCount}>
              {playlist.length} {playlist.length === 1 ? "song" : "songs"}
            </p>
          )}
          <div style={styles.videoFrameWrapper}>
            <FrameManager
              muted
              controls={false}
              style={styles.frameManager}
              hideOverlay
            />
          </div>
        </div>
        <div style={styles.playlistColumn}>
          <div style={styles.previousSongsWrapper}>
            {!!previousSongs &&
              previousSongs.map((song, index) => (
                <PlaylistItem
                  song={song}
                  key={index}
                  handleSongClick={handleSongClick}
                />
              ))}
          </div>

          {!!currentSong && (
            <PlaylistItem
              song={currentSong}
              key={400}
              handleSongClick={handleSongClick}
              highlight
            />
          )}
          <div style={styles.nextSongsWrapper}>
            {!!nextSongs &&
              nextSongs.map((song, index) => (
                <PlaylistItem
                  song={song}
                  key={index}
                  handleSongClick={handleSongClick}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
